import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Plus, Pencil, Trash2, Eye, EyeOff, Search, Package, Image, Upload, Barcode, Download, FileText, FileSpreadsheet, FileUp } from "lucide-react";
import { toast } from "sonner";
import ImageEditor from "../../components/ImageEditor";
import BarcodeScanner from "../../components/BarcodeScanner";
import { BatchImportExport } from "../../components/BatchImportExport";
import { exportProducts } from "../../utils/exportUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminProducts = () => {
  const { getAuthHeaders } = useAuth();
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [imageEditorOpen, setImageEditorOpen] = useState(false);
  const [barcodeScannerOpen, setBarcodeScannerOpen] = useState(false);
  const [batchImportOpen, setBatchImportOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    name_ar: "",
    category_id: "",
    brand: "",
    description: "",
    country_of_origin: "",
    cost_price: 0,
    sale_price: 0,
    discount: 0,
    sku: "",
    expiry_date: "",
    batch_number: "",
    stock_quantity: 0,
    min_stock_alert: 10,
    is_visible: true,
    is_featured: false,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [prodRes, catRes] = await Promise.all([
        axios.get(`${API}/products/all`, { headers }),
        axios.get(`${API}/categories`),
      ]);
      setProducts(prodRes.data);
      setCategories(catRes.data);
    } catch (error) {
      toast.error("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeaders();
      const submitData = { ...formData, images: productImages };
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, submitData, { headers });
        toast.success(t("productUpdated"));
      } else {
        await axios.post(`${API}/products`, submitData, { headers });
        toast.success(t("productCreated"));
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("failedToSave"));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await axios.delete(`${API}/products/${id}`, { headers: getAuthHeaders() });
      toast.success("Product deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const toggleVisibility = async (product) => {
    try {
      await axios.patch(`${API}/products/${product.id}/visibility`, {}, { headers: getAuthHeaders() });
      fetchData();
      toast.success(`Product ${product.is_visible ? "hidden" : "visible"}`);
    } catch (error) {
      toast.error("Failed to update visibility");
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setProductImages(product.images || []);
    setFormData({
      name: product.name || "",
      name_ar: product.name_ar || "",
      category_id: product.category_id || "",
      brand: product.brand || "",
      description: product.description || "",
      country_of_origin: product.country_of_origin || "",
      cost_price: product.cost_price || 0,
      sale_price: product.sale_price || 0,
      discount: product.discount || 0,
      sku: product.sku || "",
      expiry_date: product.expiry_date || "",
      batch_number: product.batch_number || "",
      stock_quantity: product.stock_quantity || 0,
      min_stock_alert: product.min_stock_alert || 10,
      is_visible: product.is_visible !== false,
      is_featured: product.is_featured || false,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingProduct(null);
    setProductImages([]);
    setFormData({
      name: "", name_ar: "", category_id: "", brand: "", description: "",
      country_of_origin: "", cost_price: 0, sale_price: 0, discount: 0,
      sku: "", expiry_date: "", batch_number: "", stock_quantity: 0,
      min_stock_alert: 10, is_visible: true, is_featured: false,
    });
  };

  const handleImageSave = (imageData) => {
    setProductImages(prev => [...prev, imageData]);
    toast.success(t("imageAdded"));
  };

  const removeImage = (index) => {
    setProductImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleExport = (format) => {
    try {
      const exportData = products.map(p => ({
        ...p,
        category_name: categories.find(c => c.id === p.category_id)?.name || "-"
      }));
      exportProducts(exportData, format);
      toast.success(t("reportExported"));
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const handleBarcodeFound = (product) => {
    openEditDialog(product);
  };

  const generateBarcode = async (productId) => {
    try {
      const response = await axios.post(`${API}/products/${productId}/generate-barcode`, {}, { headers: getAuthHeaders() });
      toast.success("Barcode generated!");
      fetchData();
    } catch (error) {
      toast.error("Failed to generate barcode");
    }
  };

  const filteredProducts = products.filter((p) =>
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.sku?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="admin-products">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder={t("searchProducts")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700 text-white"
            data-testid="product-search"
          />
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setBarcodeScannerOpen(true)}
            className="border-slate-600 text-slate-300"
          >
            <Barcode className="mr-2 h-4 w-4" /> {t("scan")}
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setBatchImportOpen(true)}
            className="border-slate-600 text-slate-300"
          >
            <FileUp className="mr-2 h-4 w-4" /> {t("importProducts")}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="border-slate-600 text-slate-300">
                <Download className="mr-2 h-4 w-4" /> {t("export")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-slate-800 border-slate-700">
              <DropdownMenuItem onClick={() => handleExport("pdf")} className="text-slate-300 hover:bg-slate-700 cursor-pointer">
                <FileText className="mr-2 h-4 w-4" /> PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("excel")} className="text-slate-300 hover:bg-slate-700 cursor-pointer">
                <FileSpreadsheet className="mr-2 h-4 w-4" /> Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#d4af37] text-[#0f172a] hover:bg-[#d4af37]/90" data-testid="add-product-btn">
                <Plus className="mr-2 h-4 w-4" /> {t("addProduct")}
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingProduct ? t("editProduct") : t("addProduct")}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">{t("productName")} *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="bg-slate-700 border-slate-600"
                    data-testid="product-name"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Arabic Name</Label>
                  <Input
                    value={formData.name_ar}
                    onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Category *</Label>
                  <Select value={formData.category_id} onValueChange={(v) => setFormData({ ...formData, category_id: v })}>
                    <SelectTrigger className="bg-slate-700 border-slate-600">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Brand</Label>
                  <Input
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-300">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-300">Cost Price</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.cost_price}
                    onChange={(e) => setFormData({ ...formData, cost_price: parseFloat(e.target.value) || 0 })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Sale Price *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.sale_price}
                    onChange={(e) => setFormData({ ...formData, sale_price: parseFloat(e.target.value) || 0 })}
                    required
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Discount %</Label>
                  <Input
                    type="number"
                    value={formData.discount}
                    onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) || 0 })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-300">SKU</Label>
                  <Input
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Stock Quantity</Label>
                  <Input
                    type="number"
                    value={formData.stock_quantity}
                    onChange={(e) => setFormData({ ...formData, stock_quantity: parseInt(e.target.value) || 0 })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Min Stock Alert</Label>
                  <Input
                    type="number"
                    value={formData.min_stock_alert}
                    onChange={(e) => setFormData({ ...formData, min_stock_alert: parseInt(e.target.value) || 10 })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-300">Country of Origin</Label>
                  <Input
                    value={formData.country_of_origin}
                    onChange={(e) => setFormData({ ...formData, country_of_origin: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Expiry Date</Label>
                  <Input
                    type="date"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Batch Number</Label>
                  <Input
                    value={formData.batch_number}
                    onChange={(e) => setFormData({ ...formData, batch_number: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_visible}
                    onCheckedChange={(v) => setFormData({ ...formData, is_visible: v })}
                  />
                  <Label className="text-slate-300">{t("visible")}</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(v) => setFormData({ ...formData, is_featured: v })}
                  />
                  <Label className="text-slate-300">{t("featured")}</Label>
                </div>
              </div>

              {/* Product Images Section */}
              <div className="space-y-3">
                <Label className="text-slate-300">{t("productImages")}</Label>
                <div className="flex flex-wrap gap-3">
                  {productImages.map((img, idx) => (
                    <div key={idx} className="relative group">
                      <img src={img} alt={`Product ${idx + 1}`} className="w-20 h-20 object-cover rounded-lg border border-slate-600" />
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setImageEditorOpen(true)}
                    className="w-20 h-20 border-2 border-dashed border-slate-600 rounded-lg flex flex-col items-center justify-center text-slate-400 hover:border-[#d4af37] hover:text-[#d4af37] transition-colors"
                  >
                    <Image className="w-6 h-6 mb-1" />
                    <span className="text-xs">{t("addImage")}</span>
                  </button>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-600">
                  {t("cancel")}
                </Button>
                <Button type="submit" className="bg-[#d4af37] text-[#0f172a] hover:bg-[#d4af37]/90" data-testid="save-product-btn">
                  {editingProduct ? t("update") : t("create")} {t("product")}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Image Editor Modal */}
        <ImageEditor
          isOpen={imageEditorOpen}
          onClose={() => setImageEditorOpen(false)}
          onSave={handleImageSave}
        />
        </div>
      </div>

      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-400">Loading...</div>
          ) : filteredProducts.length === 0 ? (
            <div className="p-8 text-center">
              <Package className="w-12 h-12 mx-auto text-slate-500 mb-4" />
              <p className="text-slate-400">No products found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-transparent">
                  <TableHead className="text-slate-300">Product</TableHead>
                  <TableHead className="text-slate-300">Category</TableHead>
                  <TableHead className="text-slate-300">Price</TableHead>
                  <TableHead className="text-slate-300">Stock</TableHead>
                  <TableHead className="text-slate-300">Status</TableHead>
                  <TableHead className="text-slate-300 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map((product) => (
                  <TableRow key={product.id} className="border-slate-700">
                    <TableCell>
                      <div>
                        <p className="font-medium text-white">{product.name}</p>
                        <p className="text-xs text-slate-400">{product.sku || "No SKU"}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-300">
                      {categories.find((c) => c.id === product.category_id)?.name || "-"}
                    </TableCell>
                    <TableCell>
                      <p className="text-white font-medium">{product.sale_price} LYD</p>
                      {product.discount > 0 && (
                        <Badge className="bg-red-500/20 text-red-400 border-0 text-xs">-{product.discount}%</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={product.stock_quantity <= product.min_stock_alert ? "bg-red-500/20 text-red-400" : "bg-green-500/20 text-green-400"}>
                        {product.stock_quantity}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {product.is_visible ? (
                          <Badge className="bg-green-500/20 text-green-400">Visible</Badge>
                        ) : (
                          <Badge className="bg-slate-500/20 text-slate-400">Hidden</Badge>
                        )}
                        {product.is_featured && (
                          <Badge className="bg-[#d4af37]/20 text-[#d4af37]">Featured</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => toggleVisibility(product)}
                          className="text-slate-400 hover:text-white"
                        >
                          {product.is_visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(product)}
                          className="text-slate-400 hover:text-white"
                          data-testid={`edit-product-${product.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(product.id)}
                          className="text-red-400 hover:text-red-300"
                          data-testid={`delete-product-${product.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Barcode Scanner Modal */}
      <BarcodeScanner
        isOpen={barcodeScannerOpen}
        onClose={() => setBarcodeScannerOpen(false)}
        onProductFound={handleBarcodeFound}
      />

      {/* Batch Import/Export Modal */}
      <BatchImportExport
        isOpen={batchImportOpen}
        onClose={() => setBatchImportOpen(false)}
        onImportComplete={fetchData}
      />
    </div>
  );
};

export default AdminProducts;
